import React from 'react';
import Spinner from 'react-bootstrap/Spinner';
import styled from 'styled-components';
import theme from 'v3/theme';

const StyledAddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 16px;
  background: white;
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: 6px;
  color: ${theme.colors.dark};
  font-size: 14px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: all 0.2s;

  &:hover {
    background: ${({ disabled }) => (disabled ? 'white' : theme.colors.gray[50])};
    border-color: ${({ disabled }) => (disabled ? theme.colors.gray[300] : theme.colors.gray[400])};
  }

  svg {
    font-size: 12px;
  }
`;

export const AddButton = ({ loading, disabled, children, ...props }) => {
  return (
    <StyledAddButton {...props} disabled={disabled || loading}>
      {loading ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
          />
          <span className="sr-only"> Loading...</span>
        </>
      ) : (
        children
      )}
    </StyledAddButton>
  );
};

export const TypesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TypeItemWrapper = styled.div`
  background: white;
  border: 1px solid ${theme.colors.gray[200]};
  border-radius: 8px;
  transition: all 0.2s;

  ${props => props.isDragging && `
    opacity: 0.6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  `}
`;

export const TypeItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
`;

export const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  padding: 4px 6px;
  color: ${theme.colors.gray[400]};

  &:active {
    cursor: grabbing;
  }

  svg {
    font-size: 16px;
  }
`;

export const OrderBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`;

export const TypeMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const TypeName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.dark};
`;

export const FieldCountBadge = styled.span`
  padding: 3px 10px;
  background: #dcfce7;
  color: #15803d;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

export const CustomDescription = styled.span`
  font-size: 14px;
  color: ${theme.colors.gray[500]};
  margin-left: auto;
  margin-right: 12px;
`;

export const TypeActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${props => props.danger ? theme.colors.danger : theme.colors.gray[600]};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.danger ? '#fee2e2' : theme.colors.gray[100]};
    color: ${props => props.danger ? '#dc2626' : theme.colors.dark};
  }

  ${props => props.active && `
    background: #dcfce7;
    color: #15803d;
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 14px;
  }
`;

export const ExpandedContent = styled.div`
  padding: 0 12px 16px 48px;
  border-top: 1px solid ${theme.colors.gray[100]};
  padding-top: 16px;
`;

export const FieldsTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.dark};
  margin-bottom: 12px;
`;

export const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  background: ${theme.colors.gray[50]};
  border: 1px solid ${props => props.checked ? theme.colors.gray[300] : theme.colors.gray[200]};
  border-radius: 6px;
`;

export const FieldLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const FieldMandatoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 0;
`;

export const MandatoryText = styled.span`
  color: ${theme.colors.gray[400]};
  font-size: 12px;
  font-weight: 400;
`;

export const FieldIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${props => props.checked ? theme.colors.primary : theme.colors.gray[400]};
  font-size: 16px;
`;

export const FieldName = styled.span`
  font-size: 14px;
  color: ${props => props.checked ? theme.colors.dark : theme.colors.gray[500]};
  font-weight: ${props => props.checked ? 500 : 400};
`;

export const MandatoryBadge = styled.span`
  padding: 2px 8px;
  background: ${theme.colors.gray[100]};
  color: ${theme.colors.gray[600]};
  border: 1px solid ${theme.colors.gray[200]};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  svg {
    font-size: 48px;
    color: ${theme.colors.gray[300]};
    margin-bottom: 16px;
  }

  p {
    color: ${theme.colors.gray[500]};
    font-size: 14px;
    margin: 0 0 20px 0;
  }
`;

export const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.dark};
  margin-bottom: 8px;
`;

export const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

export const ColorCircle = styled.button`
  width: ${props => props.size === 'small' ? '24px' : '40px'};
  height: ${props => props.size === 'small' ? '24px' : '40px'};
  border-radius: 50%;
  background: ${props => props.color};
  border: ${props => props.size === 'small' ? '2px' : '3px'} solid ${props => props.selected ? theme.colors.dark : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.selected ? `0 0 0 2px white, 0 0 0 ${props.size === 'small' ? '3px' : '4px'} ${theme.colors.dark}` : 'none'};

  &:hover {
    transform: scale(1.1);
  }
`;

export const ColorPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${theme.colors.gray[50]};
  border-radius: 8px;
`;

export const ColorPreviewDot = styled.div`
  width: ${props => props.size === 'small' ? '14px' : '24px'};
  height: ${props => props.size === 'small' ? '14px' : '24px'};
  border-radius: 50%;
  background: ${props => props.color};
  flex-shrink: 0;
`;

export const ColorPreviewText = styled.span`
  font-size: 14px;
  color: ${theme.colors.gray[600]};
`;
